import { FunctionComponent, useCallback, useMemo } from 'react';
import styles from '../../../styles.module.scss';
import { OccurrenceCard } from '../../../OccurrenceCard';
import Button from '../../../../../Button';
import BlueprintIcon from '../../../../../BlueprintIcon';
import {
    PublicBlendedCourse,
    PublicChildOccurrence,
    PublicClassCourse,
    PublicOccurrence,
} from '@cademy-package/sdk-component-types';

type ChildOccurrencesProps = {
    childOccurrences: Array<PublicChildOccurrence>;
    course: PublicClassCourse | PublicBlendedCourse;
    hidePricing?: boolean;
    isSoldOut: boolean;
    isWaitlistEnabled: boolean;
    occurrenceCardClickHandler: (selectedOccurrence: PublicOccurrence) => void;
    open: boolean;
    setOpen: (newValue: boolean) => void;
    showVATLabel: boolean;
    blockOccurrence?: PublicOccurrence;
};

export const ChildOccurrences: FunctionComponent<ChildOccurrencesProps> = ({
    childOccurrences,
    course,
    hidePricing,
    isSoldOut,
    isWaitlistEnabled,
    occurrenceCardClickHandler,
    open,
    setOpen,
    showVATLabel,
    blockOccurrence,
}) => {
    const bookableChildOccurrences = useMemo(() => {
        if (childOccurrences.length === 0) {
            return [];
        }
        return childOccurrences.filter((occurrence) => occurrence.individually_bookable === true);
    }, [childOccurrences]);

    const hasBookableChildren = useMemo(() => {
        return bookableChildOccurrences.length !== 0;
    }, [bookableChildOccurrences]);

    const handleViewOccurrencesButtonClick = useCallback(
        (event: any) => {
            event.stopPropagation();
            setOpen(!open);
        },
        [open, setOpen]
    );

    if (!open) {
        return (
            <div className={styles.viewOccurrencesButtonWrapper}>
                <Button
                    className={styles.viewOccurrencesButton}
                    preset="textButton"
                    onClick={handleViewOccurrencesButtonClick}
                >
                    View {hasBookableChildren ? 'Individually Bookable ' : ''}Dates{' '}
                    <BlueprintIcon
                        className={styles.viewOccurrencesButtonIcon}
                        height={10}
                        width={10}
                        icon={'ArrowDown'}
                    />
                </Button>
            </div>
        );
    }

    return (
        <ul className={styles.childOccurrenceList}>
            {childOccurrences.map((occurrence) => {
                return (
                    <OccurrenceCard
                        key={occurrence._id}
                        occurrence={occurrence}
                        blockOccurrence={blockOccurrence}
                        course={course}
                        occurrenceCardClickHandler={occurrenceCardClickHandler}
                        isSoldOut={isSoldOut}
                        isWaitlistEnabled={isWaitlistEnabled}
                        hidePricing={hidePricing}
                        showVATLabel={showVATLabel}
                    />
                );
            })}
            <li className={styles.viewOccurrencesButtonListItem}>
                <Button
                    preset="textButton"
                    className={styles.hideOccurrencesButton}
                    onClick={handleViewOccurrencesButtonClick}
                >
                    Hide {hasBookableChildren ? 'Individually Bookable ' : ''}Dates{' '}
                    <BlueprintIcon
                        className={styles.viewOccurrencesButtonIcon}
                        icon={'ArrowUp'}
                        height={10}
                        width={10}
                    />
                </Button>
            </li>
        </ul>
    );
};
